import Vue from 'vue';
import { isNull, isUndefined, isDate, isNumber, isString, toNumber, set } from 'lodash';
import debug from 'debug';
import numeral from 'numeral';
import { format, formatDistanceToNowStrict, isValid, parse, parseJSON, parseISO, formatISO, getWeek, } from 'date-fns';
import * as dateFnsLocaleHU from 'date-fns/locale/hu';
import { GettextTranslate, gettextFilters } from '@integro/gettext';
const numeral_hu = require('numeral/locales/hu');
const log = debug('itp:core:i18n');
export class I18n {
    app;
    $$supportedLocales;
    $$storageKey;
    $$translator;
    $$locale;
    filters;
    constructor(args) {
        this.app = args.app;
        this.$$storageKey = args.storageKey;
        this.$$supportedLocales = args.supportedLocales;
        this.$$translator = new GettextTranslate({
            translations: args.translations,
            defaultLanguage: 'en',
        });
        this.filters = {
            date: this.formatDate.bind(this),
            datetime: this.formatDatetime.bind(this),
            time: this.formatTime.bind(this),
            number: this.formatNumber.bind(this),
            money: this.formatMoney.bind(this),
            distance: this.formatDistance.bind(this),
        };
        this.$$locale = this.loadLocaleCode();
        if (this.app) {
            this.app.events.emit('i18n:locale', this.locale);
        }
    }
    loadLocaleCode() {
        const lc = getUserLC(this.$$supportedLocales, this.localeCodeStorageKey);
        this.$$translator.language = lc;
        log('set numeral locale to %s', lc);
        numeral.locale(lc);
        return lc;
    }
    get gettextTranslate() {
        return this.$$translator;
    }
    getLocales() {
        return this.$$supportedLocales;
    }
    setLocale(lang) {
        if (this.$$locale !== lang) {
            log('set locale to "%s"', lang);
            this.$$locale = lang;
            setUserLC(this.localeCodeStorageKey, lang);
            location.reload();
        }
    }
    get localeCodeStorageKey() {
        return `${this.$$storageKey}.lc`;
    }
    get locale() {
        return this.$$locale;
    }
    get LCID() {
        const lang = this.locale;
        return `${lang}-${lang.toUpperCase()}`;
    }
    get dateFormat() {
        return this.locale === 'hu' ? 'YYYY.MM.DD' : 'YYYY-MM-DD';
    }
    get dateFnsDateFormat() {
        return this.locale === 'hu' ? 'yyyy.MM.dd' : 'yyyy-MM-dd';
    }
    registerFilters() {
        for (const [k, v] of Object.entries(gettextFilters())) {
            Vue.filter(k, v);
        }
        for (let kv of Object.entries(this.filters)) {
            Vue.filter(...kv);
        }
    }
    translate(msgid, options) {
        return this.$$translator.translate({ msgid, ...options });
    }
    _(msgid) {
        return this.translate(msgid);
    }
    parseDate(value) {
        if (isNull(value) || isUndefined(value)) {
            return;
        }
        if (isDate(value)) {
            return value;
        }
        let parsed;
        if (isNumber(value)) {
            parsed = parse(value.toString(), 't', new Date());
        }
        if (isString(value)) {
            parsed = parseISO(value);
            if (!isValid(parsed)) {
                parsed = parseJSON(value);
            }
        }
        if (isValid(parsed)) {
            return parsed;
        }
    }
    formatDate(value, opts) {
        const d = this.parseDate(value);
        if (isUndefined(d)) {
            return '';
        }
        if (opts && opts.distanceToNow) {
            return this.formatDistance(d);
        }
        return format(d, this.dateFnsDateFormat);
    }
    stringifyDate(date) {
        return date ? formatISO(date) : null;
    }
    formatDatetime(value, opts) {
        const d = this.parseDate(value);
        if (isUndefined(d)) {
            return '';
        }
        if (opts && opts.distanceToNow) {
            return this.formatDistance(d);
        }
        return d.toLocaleDateString(this.locale, {
            hour: '2-digit',
            minute: '2-digit',
        });
    }
    formatTime(value, opts) {
        if (isString(value) && value.length === 5) {
            return value;
        }
        if (isString(value) && value.length === 8) {
            return value;
        }
        const d = this.parseDate(value);
        if (isUndefined(d)) {
            return '';
        }
        if (opts && opts.distanceToNow) {
            return this.formatDistance(d);
        }
        return d.toLocaleTimeString(this.locale, {
            hour: '2-digit',
            minute: '2-digit',
        });
    }
    formatNumber(value, opts) {
        if (isNull(value) || isUndefined(value)) {
            return '';
        }
        value = toNumber(value);
        if (isNumber(value)) {
            const options = { useGrouping: true };
            if (opts && opts.decimals) {
                set(options, 'minimumFractionDigits', opts.decimals);
                set(options, 'maximumFractionDigits', opts.decimals);
            }
            if (opts && opts.minDecimals) {
                set(options, 'minimumFractionDigits', opts.minDecimals);
            }
            if (isUndefined(options.maximumFractionDigits) || isNull(options.maximumFractionDigits)) {
                set(options, 'maximumFractionDigits', 20);
            }
            let result = Intl.NumberFormat(this.locale, options).format(value);
            if (this.locale === 'en') {
                return result.replace(/,/g, ' ');
            }
            return result;
        }
        return value;
    }
    formatMoney(value, currency, digits) {
        if (isNull(value) || isUndefined(value)) {
            return '';
        }
        if (isNumber(value)) {
            const options = {
                style: 'currency',
                currency
            };
            if (digits != null) {
                set(options, 'minimumFractionDigits', digits);
                set(options, 'maximumFractionDigits', digits);
            }
            return Intl.NumberFormat(this.locale, options).format(value);
        }
        if (isString(value)) {
            console.log("Value is not a number!", value);
        }
        return value;
    }
    formatDistance(date) {
        if (this.locale === 'hu') {
            return formatDistanceToNowStrict(date, {
                locale: dateFnsLocaleHU.default,
                addSuffix: true,
            });
        }
        return formatDistanceToNowStrict(date, {
            addSuffix: true,
        });
    }
    getWeek(date) {
        return date ? getWeek(date) : null;
    }
}
function setUserLC(storageKey, localeCode) {
    const $html = document.querySelector('html');
    if ($html) {
        $html.setAttribute('lang', localeCode);
    }
    localStorage.setItem(storageKey, localeCode);
}
function getBrowserLocale() {
    const navLang = navigator.languages !== undefined
        ? navigator.languages[0]
        : navigator.language;
    if (!navLang) {
        return;
    }
    return navLang.trim().split(/-|_/)[0];
}
function getUserLC(supportedLocales, storageKey) {
    const all = supportedLocales.map(p => p.code), valid = (code) => !!code && all.indexOf(code) > -1;
    let lc = storageKey ? localStorage.getItem(storageKey) : null;
    if (lc && valid(lc)) {
        return lc;
    }
    let lcb = getBrowserLocale();
    if (lcb && valid(lcb)) {
        return lcb;
    }
    return getFallbackLC(supportedLocales);
}
function getFallbackLC(supportedLocales) {
    for (const locale of supportedLocales) {
        if (locale.fallback) {
            return locale.code;
        }
    }
    return 'en';
}
//# sourceMappingURL=i18n.js.map